import { Component, Injector, OnDestroy, OnInit, TemplateRef, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { Company, User, UserTab } from "../../../auth/models";
import { DateTimeHelper } from '../../../common/helpers/datetime/datetime.helper';
import { CurrencyCollection, FuelPreferenceCollection, UnitsCollection } from "../../../common/models";
import { LoadingIndicatorService, ToastService } from '../../../main/services';
import { Contract, ListContracts, Round, Tender, TenderStats } from '../../models';
import { ROUND_STATUSES } from '../../models/round';
import { SuppliersService, TenderBidsService, TenderContractsService, TenderContractsStatisticService, TenderService, TenderSuppliersService } from '../../services';
import { ContractFormModalComponent } from "../create-tender-contracts";
import { AddInvitedSuppliersFormModalComponent } from "../create-tender-suppliers";

@Component({
    selector: 'jft-tender-contracts-all',
    templateUrl: './tender-contracts-all.component.html',
    styleUrls: ['./tender-contracts-all.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TenderContractsAllComponent implements OnInit, OnDestroy {
    @ViewChild('cancelLocationModal') cancelLocationModal: TemplateRef<any>;
    @ViewChild('deleteLocationModal') deleteLocationModal: TemplateRef<any>;

    public loading: boolean = false;
    public cancellingLocation: boolean = false;
    public tender: Tender;
    public contracts: ListContracts[] = [];
    public roundStatuses = ROUND_STATUSES;
    public contractToCancel: any;
    public contractToDelete: any;
    public deletingLocation: boolean = false;

    public orderField: string;
    public order: string;
    public availableTabs: UserTab[] = [];

    private termSubscription: Subscription;
    private updateContractsSubscription: Subscription;
    private tenderUpdatedSubscription: Subscription;
    public fullContract: Contract;

    public currenciesCollection: CurrencyCollection;
    public unitsCollection: UnitsCollection;
    public fuelPreferenceCollection: FuelPreferenceCollection;
    public allChecked: any;
    public suppliers: Company[] = [];

    public stats: TenderStats;
    public importStats: any;
    public showImportStatsModal: boolean = false;

    // Pagination properties
    public currentPage: number = 1;
    public pageSize: number = 30;
    public totalItems: number = 0;
    public filteredBidsCount: number = 0;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        protected toast: ToastService,
        private contractsService: TenderBidsService,
        private injector: Injector,
        private ngbModal: NgbModal,
        private contractService: TenderContractsService,
        private tenderSuppliersService: TenderSuppliersService,
        private suppliersService: SuppliersService,
        private statsService: TenderContractsStatisticService,
        public company: Company,
        private loadingIndicator: LoadingIndicatorService,
        private tenderService: TenderService,
        public user: User,
    ) {
    }

    public ngOnInit() {
        this.router.routeReuseStrategy.shouldReuseRoute = function() { return false; }; // the same url navigate init ngOnInit again

        // Check for import stats from redirection
        if (history.state.importStats) {
            this.importStats = history.state.importStats;
            this.showImportStatsModal = true;
            // Clear state to prevent showing on refresh (though standard state usually persists on refresh in some browsers, explicit handling is good)
            window.history.replaceState({}, document.title);
        }

        this.dataInit();
        this.orderInit();
        this.searchInit();
    }

    private dataInit() {
        let data = this.route.snapshot.data,
            parentData = this.route.parent.parent.snapshot.data;

        //this.contracts = data['contracts'];
        this.tender = parentData['tender'];
        this.availableTabs = data['availableTabs'];

        this.statsService.fetch(this.tender.id).subscribe((stats) => this.stats = stats);

        // Don't fetch contracts here - let parent component's loadLocationTags() trigger it
        // This ensures saved tag filters are loaded before fetching data
        // The parent will call applyFilters() which triggers updateContractsObservable

        this.updateContractsSubscription = this.contractsService.updateContractsObservable.subscribe(item => {
            // Only update contracts if it's not a stats_updated event (to prevent infinite loop)
            if (!item || (typeof item === 'object' && (item as any).type !== 'stats_updated')) {
                this.updateContracts('');
            }
        });

        this.tenderUpdatedSubscription = this.tenderService.onTenderUpdated$.subscribe(() => {
            this.updateContracts('');
        });
    }

    private searchInit() {
        this.termSubscription = this.contractsService.termObservable
            .pipe(debounceTime(500))
            .pipe(distinctUntilChanged())
            .subscribe((term: string) => {
                this.loading = true;
                this.loadingIndicator.show();

                this.contractsService.setFilter(term);
                this.contractsService.setPage(1); // Reset to first page on search
                this.contractsService.fetchPaginated(this.tender.id).subscribe((result) => {
                    if (Array.isArray(result)) {
                        this.contracts = result;
                        this.totalItems = result.length;
                        this.currentPage = 1;
                        this.filteredBidsCount = 0;
                    } else {
                        this.contracts = result.contracts;
                        this.totalItems = result.totalItems;
                        this.currentPage = result.currentPage;
                        this.filteredBidsCount = result.filteredBidsCount || 0;
                    }
                    this.loading = false;
                    this.loadingIndicator.hide();
                }, (error) => {
                    this.loading = false;
                    this.loadingIndicator.hide();
                    this.toast.error(error, this.tender.name);
                });
            });
    }

    private orderInit() {
        let order = this.contractsService.getOrder();
        if (order) {
            this.order = order.startsWith('-') ? 'DESC' : 'ASC';
            this.orderField = order.startsWith('-') ? order.slice(1) : order;
        } else {
            // Set default sorting to location alphabetically
            this.order = 'ASC';
            this.orderField = 'location';
            this.contractsService.setOrder('location', 'ASC');
        }
    }

    public sort($event: Event, field: string) {
        $event.preventDefault();
        $event.stopPropagation();
        if (this.orderField == field) {
            this.order = this.order == 'ASC' ? 'DESC' : 'ASC';
        } else {
            this.order = 'ASC';
        }
        this.orderField = field;

        this.loading = true;
        this.loadingIndicator.show();

        this.contractsService.setOrder(this.orderField, this.order).fetchPaginated(this.tender.id).subscribe((result) => {
            if (Array.isArray(result)) {
                this.contracts = result;
                this.totalItems = result.length;
                this.currentPage = 1;
                this.filteredBidsCount = 0;
            } else {
                this.contracts = result.contracts;
                this.totalItems = result.totalItems;
                this.currentPage = result.currentPage;
                this.filteredBidsCount = result.filteredBidsCount || 0;
            }
            this.loading = false;
            this.loadingIndicator.hide();
        }, (e) => {
            this.loading = false;
            this.loadingIndicator.hide();
            this.toast.error(e, this.tender.name);
        });
    }

    public updateContracts(successMsg = 'Round has been extended') {
        this.loading = true;
        this.loadingIndicator.show();

        this.contractsService.fetchPaginated(this.tender.id).subscribe(
            (result: any) => {
                if (Array.isArray(result)) {
                    this.contracts = result;
                    this.totalItems = result.length;
                    this.currentPage = 1;
                    this.filteredBidsCount = 0;
                } else {
                    this.contracts = result.contracts;
                    this.totalItems = result.totalItems;
                    this.currentPage = result.currentPage;
                    this.filteredBidsCount = result.filteredBidsCount || 0;
                }
                // Refresh stats (total bids submitted) whenever contracts update
                this.statsService.fetch(this.tender.id).subscribe((stats) => {
                    this.stats = stats;
                    // Notify parent component (tender-bids) to refresh its stats
                    // Use 'stats_updated' type to prevent infinite loop
                    this.contractsService.updateContractsObservable.next({
                        type: 'stats_updated',
                        stats: stats
                    });
                });
                if (successMsg) {
                    this.toast.success(successMsg);
                }
                this.loading = false;
                this.loadingIndicator.hide();
            }, (e) => {
                this.toast.error(e, this.tender.name);
                this.loading = false;
                this.loadingIndicator.hide();
            }
        );
    }

    public _editLocation(contract: any) {
        const dialogRef = this.ngbModal.open(ContractFormModalComponent, {
            injector: Injector.create([
                {provide: Contract, useValue: contract},
                {provide: Tender, useValue: this.tender},
                {provide: CurrencyCollection, useValue: this.currenciesCollection},
                {provide: UnitsCollection, useValue: this.unitsCollection},
                {provide: FuelPreferenceCollection, useValue: this.fuelPreferenceCollection}
            ], this.injector),
            size: 'lg',
            backdrop: 'static'
        })

        dialogRef.componentInstance.disableLocation = true;
        dialogRef.result.then((contract: Contract) => {
            this.updateContracts();
        }, (error) => {

        });
    }

    public editLocation(contract: any) {
       this.contractService.fetchItem(this.tender.id, contract.id).subscribe((contract: Contract) => {
            this.fullContract = contract;
            this._editLocation(contract);
        });
    }

    public showCancelConfirmation(contract: any) {
        this.contractToCancel = contract;
        this.ngbModal.open(this.cancelLocationModal);
    }

    public showDeleteConfirmation(contract: any) {
        this.contractToDelete = contract;
        this.ngbModal.open(this.deleteLocationModal);
    }

    public confirmDeleteContract() {
        if (this.contractToDelete) {
            // Check if location is cancelled - only cancelled locations can be deleted
            if (this.contractToDelete.currentRound?.status !== this.roundStatuses.CANCELLED) {
                this.toast.error('Only cancelled locations can be deleted. Please cancel the location first.');
                this.ngbModal.dismissAll();
                this.contractToDelete = null;
                return;
            }

            this.deletingLocation = true;
            this.contractService.delete(this.tender, this.contractToDelete).subscribe((data) => {
                this.deletingLocation = false;
                this.updateContracts('Location has been deleted');
                this.ngbModal.dismissAll();
                this.contractToDelete = null;
            }, (e) => {
                this.deletingLocation = false;
                this.toast.error(e);
                this.ngbModal.dismissAll();
                this.contractToDelete = null;
            });
        }
    }

    public confirmCancelContract() {
        if (this.contractToCancel) {
            // Check if location is awarded - shouldn't happen due to disabled button, but safety check
            if (this.contractToCancel.currentRound?.status === this.roundStatuses.AWARDED) {
                this.toast.error('Cannot cancel awarded location. Please withdraw the award first.');
                return;
            }

            this.cancellingLocation = true;
            this.contractService.cancel(this.tender, this.contractToCancel).subscribe((contract) => {
                this.cancellingLocation = false;
                this.updateContracts('Location has been cancelled');
                this.ngbModal.dismissAll();
                this.contractToCancel = null;
            }, (e) => {
                this.cancellingLocation = false;
                this.toast.error(e);
                this.ngbModal.dismissAll();
                this.contractToCancel = null;
            });
        }
    }

    public cancelContract(contract){
        this.contractService.cancel(this.tender, contract).subscribe((contract) => {
            this.updateContracts('Location has been cancelled');
        }, (e) => {
            this.toast.error(e);
        });
    }

    public onAddSupplier(contract: any){
        this.suppliersService.fetchUsers().subscribe(suppliers => {
            this.suppliers = suppliers;
            this.addSupplierModal(contract)
        })
    }

    public addSupplierModal(contract: any) {
        const modal: NgbModalRef = this.ngbModal.open(AddInvitedSuppliersFormModalComponent, {
            injector: Injector.create([
                {provide: Tender, useValue: this.tender},
            ], this.injector),
            fullscreen: true,
            backdrop: 'static'
        })
        modal.result.then((data) => {
            this.addSuppliersToLaunchedTenderContracts(data.users, contract);
        }, (error) => {});
        modal.componentInstance.availableTags = this.availableTabs;
        modal.componentInstance.showAllUsers = true;
        modal.componentInstance.allSuppliersButton = false;
    }

    private addSuppliersToLaunchedTenderContracts(suppliers: Company[], contract) {
        suppliers.forEach(supplier => {
            this.addSupplierToLaunchedTenderContract(supplier, contract);
        })
    }

    private addSupplierToLaunchedTenderContract(item: Company, contract) {
        this.tenderSuppliersService.addSupplierToLaunchedTenderContracts(this.tender, item, contract).subscribe(data => {
            if (data['details']){
                this.toast.success(data['details']);
            } else {
                this.toast.success(`A new supplier ${item.name ? item.name : item['email']} has been added`);
            }
        }, (e) => {
            this.toast.error(e);
        });
    }

    public ngOnDestroy(): void {
        this.termSubscription.unsubscribe();
        this.updateContractsSubscription.unsubscribe();
        if (this.tenderUpdatedSubscription) {
            this.tenderUpdatedSubscription.unsubscribe();
        }
    }

    public isDateExpired(date) {
        return DateTimeHelper.isDateExpired(date)
    }

    public onAllChecked(event: any) {
        // this._addIsCheckedColumn(this.allChecked)
        // this.fetchSelectedItems();
    }

    public changeSelection() {
        this.allChecked = false;
        //this.fetchSelectedItems()
    }

    public onRoundExtend(round: Round) {
        this.updateContracts();
    }

    // Pagination methods
    public loadMore() {
        this.currentPage++;
        this.contractsService.setPage(this.currentPage);
        this.loading = true;
        this.loadingIndicator.show();

        this.contractsService.fetchPaginated(this.tender.id).subscribe((result) => {
            let newContracts = [];
            if (Array.isArray(result)) {
                newContracts = result;
                this.totalItems = result.length;
                this.filteredBidsCount = 0;
            } else {
                newContracts = result.contracts;
                this.totalItems = result.totalItems;
                this.filteredBidsCount = result.filteredBidsCount || 0;
            }

            this.contracts = [...this.contracts, ...newContracts];

            this.loading = false;
            this.loadingIndicator.hide();
        }, (error) => {
            this.loading = false;
            this.loadingIndicator.hide();
            this.toast.error(error, this.tender.name);
        });
    }

    public onPageSizeChange(pageSize: number) {
        this.pageSize = pageSize;
        this.currentPage = 1; // Reset to first page
        this.contractsService.setPageSize(pageSize).setPage(1);
        this.loading = true;
        this.loadingIndicator.show();

        this.contractsService.fetchPaginated(this.tender.id).subscribe((result) => {
            if (Array.isArray(result)) {
                this.contracts = result;
                this.totalItems = result.length;
                this.currentPage = 1;
                this.filteredBidsCount = 0;
            } else {
                this.contracts = result.contracts;
                this.totalItems = result.totalItems;
                this.currentPage = result.currentPage;
                this.filteredBidsCount = result.filteredBidsCount || 0;
            }
            this.loading = false;
            this.loadingIndicator.hide();
        }, (error) => {
            this.loading = false;
            this.loadingIndicator.hide();
            this.toast.error(error, this.tender.name);
        });
    }

    // Helper methods for pagination display
    public get showingCount(): number {
        return this.contracts.length;
    }
    public closeImportStatsModal() {
        this.showImportStatsModal = false;
        this.importStats = null;
    }
}
